/**
 * <header-dropdown> Web Component
 *
 * Handles dropdown behavior for header navigation menu items.
 *
 * Desktop: hover to open/close with a small debounce delay.
 *   Dropdown panels live OUTSIDE the component and are matched via data-menu-num.
 * Mobile: click to open a right-sliding sub-drawer panel (child of component).
 * Supports nested sub-drawers for mega dropdown level-3 navigation.
 *
 * Usage (desktop):
 *   <header-dropdown data-menu-num="1">
 *     <a class="header-dropdown__trigger">Menu Item</a>
 *   </header-dropdown>
 *   <!-- panel is separate -->
 *   <div class="header-dropdown__panel" data-menu-num="1">...</div>
 *
 * Usage (mobile):
 *   <header-dropdown data-mobile data-menu-num="1">
 *     <button class="header-dropdown__trigger">Menu Item</button>
 *     <div class="header-dropdown__panel header-dropdown__panel--mobile-subdrawer">...</div>
 *   </header-dropdown>
 */
class HeaderDropdown extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.panel = null;
    this.closeTimeout = null;
    this.megaWrapper = null;
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPanelMouseEnter = this.onPanelMouseEnter.bind(this);
    this.onPanelMouseLeave = this.onPanelMouseLeave.bind(this);
    this.updatePositionBound = this.updatePosition.bind(this);
  }

  syncHeaderDropdownState() {
    const hasActiveDesktopDropdown = Boolean(
      document.querySelector('header-dropdown[open]:not([data-mobile])') ||
      document.querySelector('.header-dropdown__panel--simple.is-active') ||
      document.querySelector('.header-dropdown__panel--mega.is-active'),
    );

    document.body.classList.toggle(
      'header-dropdown-active',
      hasActiveDesktopDropdown,
    );
    document.dispatchEvent(new CustomEvent('header-dropdown:toggle'));
  }

  connectedCallback() {
    this.trigger = this.querySelector('.header-dropdown__trigger');
    if (!this.trigger) return;

    const menuNum = this.dataset.menuNum;

    // Desktop: find external panel by data-menu-num
    // Mobile: find internal panel (child of this element)
    if (this.hasAttribute('data-mobile')) {
      this.panel = this.querySelector('.header-dropdown__panel');
    } else if (menuNum) {
      // Find external panel that matches this menu-num (not inside a header-dropdown)
      this.panel = document.querySelector(
        `.header-dropdown__panel[data-menu-num="${menuNum}"]:not(header-dropdown .header-dropdown__panel)`,
      );
    }

    if (!this.panel) return;

    // Cache mega wrapper reference
    this.megaWrapper = document.querySelector('.mega-dropdown-wrapper');

    // Desktop: hover
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);

    // Keep desktop panel open when hovering over it
    if (!this.hasAttribute('data-mobile')) {
      this.panel.addEventListener('mouseenter', this.onPanelMouseEnter);
      this.panel.addEventListener('mouseleave', this.onPanelMouseLeave);
    }

    // Mobile: click trigger to open sub-drawer
    this.trigger.addEventListener('click', this.onTriggerClick);

    // Keyboard
    this.addEventListener('keydown', this.onKeyDown);

    // Back buttons inside sub-drawers
    this.panel.querySelectorAll('.header-dropdown__back').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    if (this.panel && !this.hasAttribute('data-mobile')) {
      this.panel.removeEventListener('mouseenter', this.onPanelMouseEnter);
      this.panel.removeEventListener('mouseleave', this.onPanelMouseLeave);
    }
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.onTriggerClick);
    }
    this.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.updatePositionBound);
    clearTimeout(this.closeTimeout);
  }

  onMouseEnter() {
    if (!this.desktopQuery.matches) return;
    clearTimeout(this.closeTimeout);
    this.open();
  }

  onMouseLeave() {
    if (!this.desktopQuery.matches) return;
    this.closeTimeout = setTimeout(() => this.close(), 150);
  }

  onPanelMouseEnter() {
    if (!this.desktopQuery.matches) return;
    clearTimeout(this.closeTimeout);
  }

  onPanelMouseLeave() {
    if (!this.desktopQuery.matches) return;
    this.closeTimeout = setTimeout(() => this.close(), 150);
  }

  onTriggerClick(e) {
    if (this.desktopQuery.matches) return;
    e.preventDefault();
    if (this.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.trigger?.focus();
    }
  }

  /**
   * Dynamically position the desktop panel below the header.
   * Simple dropdown: centered horizontally on the trigger.
   * Mega dropdown: full width, just needs top.
   */
  updatePosition() {
    if (!this.panel || !this.desktopQuery.matches) return;

    const body = document.body;
    const header = document.querySelector('.header');
    if (!header) return;

    const headerRect = header.getBoundingClientRect();
    let topPos = headerRect.bottom;

    // Keep dropdown anchored to the pre-hide header baseline even when
    // sticky-header animation translates the header up to hide announcement bar.
    if (
      body.classList.contains('header-scrolling-down') &&
      body.dataset.hideAnnouncement === 'true'
    ) {
      const announcementHeight = parseFloat(
        getComputedStyle(body)
          .getPropertyValue('--announcement-bar-height')
          .trim(),
      );

      if (!Number.isNaN(announcementHeight) && announcementHeight > 0) {
        topPos += announcementHeight;
      }
    }

    this.panel.style.top = `${topPos}px`;

    // Simple dropdown: center on trigger
    if (this.panel.classList.contains('header-dropdown__panel--simple')) {
      const triggerRect = this.trigger.getBoundingClientRect();
      const panelWidth = this.panel.offsetWidth;
      let leftPos = triggerRect.left + triggerRect.width / 2 - panelWidth / 2;

      // Clamp to viewport edges
      if (leftPos < 8) leftPos = 8;
      if (leftPos + panelWidth > window.innerWidth - 8) {
        leftPos = window.innerWidth - panelWidth - 8;
      }

      this.panel.style.left = `${leftPos}px`;
    }

    // Mega dropdown: position the wrapper
    if (
      this.panel.classList.contains('header-dropdown__panel--mega') &&
      this.megaWrapper
    ) {
      this.megaWrapper.style.top = `${topPos}px`;
    }
  }

  /**
   * Relocate banners from the hidden data container into mega dropdown panels.
   * Desktop: moves banners into .header-dropdown__mega-inner (matched by order).
   * Mobile: clones banners into .mobile-mega__banners-container (matched by data-menu-num).
   */
  relocateBanners() {
    const dataContainer = document.querySelector(
      '.header-dropdown-blocks-data',
    );
    if (!dataContainer) return;

    // Source lives in a <template> so its images stay unloaded until needed;
    // fall back to the element itself if it's a plain container.
    const dataRoot = dataContainer.content || dataContainer;
    const allBannerSources = dataRoot.querySelectorAll(
      '.mega-dropdown-banners',
    );
    if (!allBannerSources.length) return;

    const menuNum = this.dataset.menuNum;

    // Desktop mega panel: move banners by order
    if (
      !this.hasAttribute('data-mobile') &&
      this.panel.classList.contains('header-dropdown__panel--mega')
    ) {
      const allMegaPanels = document.querySelectorAll(
        '.header-dropdown__panel--mega[data-menu-num]',
      );
      let megaIndex = -1;
      allMegaPanels.forEach((panel, i) => {
        if (panel === this.panel) megaIndex = i;
      });

      if (megaIndex >= 0 && megaIndex < allBannerSources.length) {
        const bannerSource = allBannerSources[megaIndex];
        const megaInner = this.panel.querySelector(
          '.header-dropdown__mega-inner',
        );
        if (megaInner && bannerSource.children.length > 0) {
          const cloned = bannerSource.cloneNode(true);
          while (cloned.firstElementChild) {
            megaInner.appendChild(cloned.firstElementChild);
          }
        }
      }
    }

    // Mobile mega: clone banners into the mobile container
    if (this.hasAttribute('data-mobile') && menuNum) {
      const mobileContainer = this.panel.querySelector(
        `.mobile-mega__banners-container[data-menu-num="${menuNum}"]`,
      );
      if (mobileContainer) {
        // Find the matching banner source by mega index
        const allMegaPanels = document.querySelectorAll(
          '.header-dropdown__panel--mega[data-menu-num]',
        );
        let megaIndex = -1;
        let idx = 0;
        allMegaPanels.forEach((panel) => {
          if (panel.dataset.menuNum === menuNum) megaIndex = idx;
          idx++;
        });

        if (megaIndex >= 0 && megaIndex < allBannerSources.length) {
          const bannerSource = allBannerSources[megaIndex];
          if (bannerSource.children.length > 0) {
            const cloned = bannerSource.cloneNode(true);
            // Add padding and flex column layout for mobile
            cloned.classList.add('p-4', 'flex', 'flex-col', 'gap-4');
            while (cloned.firstElementChild) {
              mobileContainer.appendChild(cloned.firstElementChild);
            }
          }
        }
      }
    }
  }

  open() {
    // Relocate banners on first open so their images are never fetched
    // during initial page load (the menu may never be opened).
    if (!this._bannersRelocated) {
      this.relocateBanners();
      this._bannersRelocated = true;
    }

    this.setAttribute('open', '');
    this.trigger?.setAttribute('aria-expanded', 'true');

    const isMega = this.panel.classList.contains(
      'header-dropdown__panel--mega',
    );
    const isDesktop =
      this.desktopQuery.matches && !this.hasAttribute('data-mobile');

    if (isDesktop && isMega && this.megaWrapper) {
      // Cancel any pending close transition handler
      if (this.megaWrapper._closeHandler) {
        this.megaWrapper.removeEventListener(
          'transitionend',
          this.megaWrapper._closeHandler,
        );
        this.megaWrapper._closeHandler = null;
      }

      // Close any other active mega panel first
      this.megaWrapper
        .querySelectorAll('.header-dropdown__panel--mega.is-active')
        .forEach((p) => p.classList.remove('is-active'));

      // Show the panel so we can measure it
      this.panel.classList.add('is-active');

      // Position wrapper below header
      this.updatePosition();

      // Use rAF to ensure layout is computed after display change
      requestAnimationFrame(() => {
        setTimeout(() => {
          const contentHeight = this.panel.scrollHeight;
          this.megaWrapper.classList.add('is-active');
          this.megaWrapper.style.height = `${contentHeight}px`;
        }, 50);
      });

      window.addEventListener('resize', this.updatePositionBound);
    } else {
      this.panel.classList.add('is-active');

      if (isDesktop) {
        this.updatePosition();
        window.addEventListener('resize', this.updatePositionBound);
      }
    }

    if (isDesktop) {
      this.syncHeaderDropdownState();
    }
  }

  close() {
    this.removeAttribute('open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    clearTimeout(this.closeTimeout);

    const isMega = this.panel.classList.contains(
      'header-dropdown__panel--mega',
    );

    if (isMega && this.megaWrapper) {
      // Check if another mega panel is still active (switching between megas)
      const otherActive = this.megaWrapper.querySelector(
        '.header-dropdown__panel--mega.is-active:not([data-menu-num="' +
          this.dataset.menuNum +
          '"])',
      );

      if (otherActive) {
        // Another mega is active — just hide this panel, wrapper stays open
        this.panel.classList.remove('is-active');
      } else {
        // No other mega active — collapse the wrapper
        this.megaWrapper.style.height = '0';

        const onTransitionEnd = (e) => {
          // Only react to height transition on the wrapper itself
          if (e.propertyName !== 'height') return;
          this.panel.classList.remove('is-active');
          this.megaWrapper.classList.remove('is-active');
          this.syncHeaderDropdownState();
          this.megaWrapper.removeEventListener(
            'transitionend',
            onTransitionEnd,
          );
          this.megaWrapper._closeHandler = null;
        };

        // Store reference so open() can cancel it
        this.megaWrapper._closeHandler = onTransitionEnd;
        this.megaWrapper.addEventListener('transitionend', onTransitionEnd);
      }
    } else {
      this.panel.classList.remove('is-active');
    }

    window.removeEventListener('resize', this.updatePositionBound);

    // Also close any open sub-drawers
    this.panel
      ?.querySelectorAll('.header-dropdown__subdrawer.is-active')
      .forEach((sd) => {
        sd.classList.remove('is-active');
      });

    if (this.desktopQuery.matches && !this.hasAttribute('data-mobile')) {
      this.syncHeaderDropdownState();
    }
  }
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown);
}
